// Shared pricing + plan data used by the signup / plan-selection experience.

// Default tier configurations (prices overridden by live Stripe pricing when available)
export const DEFAULT_TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 99,
    description: 'Perfect for small teams getting started',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Asset management',
      'Job scheduling',
      'Digital dockets',
      'Customer & site management',
      'System forms (read-only)',
      '1 Subcontractor (own company)',
      'Cloud Storage: 10 GB',
      'Help Centre & Support (Standard)'
    ],
    extraUserPrice: 49,
    extraOperatorPrice: 19,
    popular: false
  },
  {
    id: 'advanced',
    name: 'Advanced',
    price: 169,
    description: 'For growing businesses needing more power',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Everything in Basic, plus:',
      'Reports & analytics',
      'Compliance tracking',
      'Compliance packs',
      'Timesheets',
      'Team messaging',
      'Quotes',
      'Custom forms builder',
      'Multiple subcontractors',
      'Cloud Storage: 50 GB',
      'Help Centre & Support (Priority)'
    ],
    extraUserPrice: 69,
    extraOperatorPrice: 29,
    popular: true
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 199,
    description: 'Full access with AI and advanced features',
    baseFeatures: [
      '1 User',
      '2 Operators',
      'Everything in Advanced, plus:',
      'Quote templates',
      'AI-powered features',
      'Smart suggestions',
      'OCR docket scanning',
      'API access (Coming Soon)',
      'Cloud Storage: 100 GB',
      'Help Centre & Support (Priority+)'
    ],
    extraUserPrice: 99,
    extraOperatorPrice: 29,
    popular: false
  }
];

// Feature comparison data for the matrix
export const FEATURE_COMPARISON = [
  {
    category: 'Core Features',
    features: [
      { name: 'Asset Management', basic: true, advanced: true, professional: true },
      { name: 'Operator Management', basic: true, advanced: true, professional: true },
      { name: 'Job Scheduling', basic: true, advanced: true, professional: true },
      { name: 'Digital Dockets', basic: true, advanced: true, professional: true },
      { name: 'Customer & Site Management', basic: true, advanced: true, professional: true },
      { name: 'Worker Types', basic: true, advanced: true, professional: true },
      { name: 'System Forms', basic: true, advanced: true, professional: true },
      { name: 'Subcontractors', basic: '1 (Own company)', advanced: 'Unlimited', professional: 'Unlimited' },
    ]
  },
  {
    category: 'Advanced Features',
    features: [
      { name: 'Standard Reports', basic: false, advanced: true, professional: true },
      { name: 'Compliance Tracking', basic: false, advanced: true, professional: true },
      { name: 'Compliance Packs', basic: false, advanced: true, professional: true },
      { name: 'Timesheets', basic: false, advanced: true, professional: true },
      { name: 'Custom Forms Builder', basic: false, advanced: true, professional: true },
      { name: 'Team Messaging', basic: false, advanced: true, professional: true },
      { name: 'Quotes', basic: false, advanced: true, professional: true },
    ]
  },
  {
    category: 'Professional Features',
    features: [
      { name: 'Quote Templates', basic: false, advanced: false, professional: true },
      { name: 'Premium Reports', basic: false, advanced: false, professional: true },
      { name: 'AI-Powered Features', basic: false, advanced: false, professional: true },
      { name: 'Smart Suggestions', basic: false, advanced: false, professional: true },
      { name: 'OCR Docket Scanning', basic: false, advanced: false, professional: true },
    ]
  },
  {
    category: 'Storage & Support',
    features: [
      { name: 'Document Storage', basic: '10 GB', advanced: '50 GB', professional: '100 GB' },
      { name: 'Storage Add-ons', basic: true, advanced: true, professional: true },
      { name: 'Help Centre', basic: true, advanced: true, professional: true },
      { name: 'Support', basic: 'Standard', advanced: 'Priority', professional: 'Priority+' },
    ]
  }
];

// Country data with phone codes and states/provinces (for quote requests)
export const COUNTRIES = [
  { code: 'AU', name: 'Australia', phoneCode: '+61', states: ['Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'] },
  { code: 'NZ', name: 'New Zealand', phoneCode: '+64', states: ['Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\'s Bay', 'Manawatu-Wanganui', 'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman', 'Waikato', 'Wellington', 'West Coast'] },
  { code: 'US', name: 'United States', phoneCode: '+1', states: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'] },
  { code: 'CA', name: 'Canada', phoneCode: '+1', states: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'] },
  { code: 'GB', name: 'United Kingdom', phoneCode: '+44', states: ['England', 'Scotland', 'Wales', 'Northern Ireland'] },
  { code: 'IE', name: 'Ireland', phoneCode: '+353', states: ['Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry', 'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'] },
  { code: 'SG', name: 'Singapore', phoneCode: '+65', states: ['Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'] },
  { code: 'AE', name: 'United Arab Emirates', phoneCode: '+971', states: ['Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'] },
  { code: 'ZA', name: 'South Africa', phoneCode: '+27', states: ['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'] },
  { code: 'IN', name: 'India', phoneCode: '+91', states: ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'] },
  { code: 'DE', name: 'Germany', phoneCode: '+49', states: ['Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'] },
  { code: 'FR', name: 'France', phoneCode: '+33', states: ['Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur'] },
  { code: 'JP', name: 'Japan', phoneCode: '+81', states: ['Hokkaido', 'Tohoku', 'Kanto', 'Chubu', 'Kinki', 'Chugoku', 'Shikoku', 'Kyushu'] },
  { code: 'OTHER', name: 'Other', phoneCode: '', states: [] }
];
